import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { MonitoringType } from 'routes/Dashboard/Monitoring/enums';

type LegendItem = {
  from?: number | string | null;
  to?: number | string | null;
  color?: unknown;
};

// Returns array-level Yup validation that aggregates all errors
const getLegendFormFieldValidations = ({
  t,
  monitoringType,
}: {
  t: TFunction;
  monitoringType: MonitoringType;
}) =>
  Yup.array()
    .min(1, t('validation-ranges-at-least-one'))
    .test(
      'ranges-integrity',
      t('validation-ranges-integrity-error'),
      function rangesIntegrity(value?: LegendItem[]) {
        const { createError, path } = this;
        const errors: Yup.ValidationError[] = [];

        if (!Array.isArray(value)) return true;

        const isAnomaly =
          monitoringType === MonitoringType.Anomaly ||
          String(monitoringType).toLowerCase() === 'anomaly';

        // Normalize items for easier checks
        const normalized = value.map((it) => {
          const rawFrom = (it?.from ?? '').toString().trim();
          const rawTo =
            it?.to === null ? null : (it?.to ?? '').toString().trim();
          const hasFrom = rawFrom !== '';
          const hasTo = rawTo !== '' && rawTo !== null;
          const fromNum = hasFrom ? Number(rawFrom) : NaN;
          const toNum = hasTo ? Number(rawTo as string) : NaN;
          return { hasFrom, hasTo, fromNum, toNum, color: it?.color };
        });

        // Determine logical last index (ignore trailing empty placeholder rows)
        let logicalLastIndex = -1;
        normalized.forEach((item, idx) => {
          if (item.hasFrom || item.hasTo) logicalLastIndex = idx;
        });
        if (logicalLastIndex === -1) return true; // handled by .min already

        const pushFieldError = (
          idx: number,
          field: 'from' | 'to' | 'color',
          message: string,
        ) => {
          errors.push(
            createError({ path: `${path}[${idx}].${field}`, message }),
          );
        };

        // Check for orphaned colors (items with color but no from/to values)
        for (let i = 0; i < normalized.length; i += 1) {
          const item = normalized[i];
          const hasColor =
            item.color !== undefined &&
            item.color !== null &&
            item.color !== '';
          if (hasColor && !item.hasFrom && !item.hasTo) {
            pushFieldError(i, 'from', t('required-field-error'));
          }
        }

        for (let i = 0; i <= logicalLastIndex; i += 1) {
          const curr = normalized[i];
          const prev = i > 0 ? normalized[i - 1] : undefined;
          const isLastLogical = i === logicalLastIndex;

          // color required (keep parity with previous behavior that required color)
          if (
            curr.color === undefined ||
            curr.color === null ||
            curr.color === ''
          ) {
            pushFieldError(i, 'color', t('required-field-error'));
          }

          // from validation
          if (!curr.hasFrom) {
            pushFieldError(i, 'from', t('required-field-error'));
          }

          // to validation
          if (isLastLogical && isAnomaly) {
            // last can omit 'to', but if provided must be valid
            if (curr.hasTo) {
              // if provided, it should be >= from (checked below when both present)
            }
          } else if (!curr.hasTo) {
            pushFieldError(i, 'to', t('required-field-error'));
          }

          // internal cross-check when both numbers are valid
          if (
            !Number.isNaN(curr.fromNum) &&
            !Number.isNaN(curr.toNum) &&
            curr.hasFrom &&
            curr.hasTo
          ) {
            if (curr.toNum < curr.fromNum) {
              pushFieldError(i, 'to', t('validation-ranges-to-gte-from'));
            }
          }

          // sequence continuity
          if (!Number.isNaN(curr.fromNum)) {
            if (i === 0) {
              if (curr.fromNum !== 0)
                pushFieldError(
                  i,
                  'from',
                  t('validation-ranges-first-from-must-be-zero'),
                );
            } else if (prev) {
              if (
                prev.hasTo &&
                !Number.isNaN(prev.toNum) &&
                curr.fromNum !== prev.toNum + 1
              ) {
                pushFieldError(
                  i,
                  'from',
                  t('validation-ranges-from-equals-prev-to-plus-one', {
                    expected: (prev.toNum as number) + 1,
                  }),
                );
              }
            }
          }
        }

        return errors.length ? new Yup.ValidationError(errors) : true;
      },
    );

export default getLegendFormFieldValidations;
